import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, Inbox } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function fmt(d) {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    return dt.toLocaleString();
  } catch {
    return String(d);
  }
}

export default function Admin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [active, setActive] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/contact`);
      setRows(res.data || []);
      if ((res.data || []).length > 0) setActive(res.data[0]);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load submissions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[var(--xw-bg)]">
      <header className="sticky top-0 z-30 bg-[var(--xw-bg)]/90 backdrop-blur border-b border-[var(--xw-line)]">
        <div className="xw-container h-[68px] flex items-center justify-between">
          <Link
            to="/"
            data-testid="admin-back-link"
            className="flex items-center gap-2 font-mono-xw text-[12px] uppercase tracking-[0.2em]"
          >
            <ArrowLeft size={14} />
            Back to Site
          </Link>
          <span className="font-display font-black uppercase tracking-tight text-[17px]">
            Xterior<span className="text-[var(--xw-orange)]">/</span>Works
            <span className="ml-2 font-mono-xw text-[11px] tracking-[0.2em] text-[var(--xw-muted)]">
              ADMIN
            </span>
          </span>
          <button
            type="button"
            onClick={load}
            data-testid="admin-refresh-button"
            className="btn-outline !py-2 !px-3 text-[12px]"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>
      </header>

      <main className="xw-container py-10">
        <div className="flex items-baseline justify-between mb-8">
          <h1 className="font-display font-black uppercase tracking-[-0.02em] text-4xl md:text-5xl">
            Submissions
          </h1>
          <span className="font-mono-xw text-[11px] tracking-[0.22em] text-[var(--xw-muted)]">
            {rows.length} TOTAL
          </span>
        </div>

        {loading && (
          <div data-testid="admin-loading" className="py-20 text-center text-[var(--xw-muted)]">
            Loading submissions…
          </div>
        )}

        {error && !loading && (
          <div className="border border-[var(--xw-line)] bg-white p-6 text-[var(--xw-orange)]">
            {error}
          </div>
        )}

        {!loading && !error && rows.length === 0 && (
          <div
            data-testid="admin-empty-state"
            className="border border-[var(--xw-line)] bg-white p-10 text-center"
          >
            <Inbox className="mx-auto mb-3" />
            <div className="font-display font-bold text-xl">No submissions yet.</div>
            <div className="text-[var(--xw-muted)] text-sm mt-2">
              Submissions from the contact form will appear here.
            </div>
          </div>
        )}

        {!loading && !error && rows.length > 0 && (
          <div className="grid grid-cols-12 gap-0 border border-[var(--xw-line)] bg-white">
            <div className="col-span-12 lg:col-span-5 border-r border-[var(--xw-line)] max-h-[70vh] overflow-y-auto">
              {rows.map((r) => {
                const isActive = active?.id === r.id;
                return (
                  <button
                    key={r.id}
                    type="button"
                    data-testid={`admin-row-${r.id}`}
                    onClick={() => setActive(r)}
                    className={`block w-full text-left p-5 border-b border-[var(--xw-line-soft)] transition-colors ${
                      isActive ? "bg-[var(--xw-ink)] text-white" : "hover:bg-[var(--xw-bg)]"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-display font-bold text-[15px] tracking-tight">
                        {r.first_name} {r.last_name}
                      </span>
                      <span className="font-mono-xw text-[10px] tracking-[0.18em] opacity-70">
                        {fmt(r.created_at)}
                      </span>
                    </div>
                    <div className="text-[13px] mt-1 opacity-80">{r.business}</div>
                    <div className="text-[12px] mt-1 opacity-60">
                      {r.role} · {r.property_type}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="col-span-12 lg:col-span-7 p-8">
              {active ? (
                <div data-testid="admin-detail">
                  <div className="font-mono-xw text-[10px] tracking-[0.22em] uppercase text-[var(--xw-muted)] mb-2">
                    Submission · {fmt(active.created_at)}
                  </div>
                  <h2 className="font-display font-black uppercase text-3xl tracking-tight">
                    {active.first_name} {active.last_name}
                  </h2>
                  <div className="text-[var(--xw-ink-soft)] mt-1">
                    {active.business}
                  </div>

                  <dl className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[
                      ["Role", active.role],
                      ["Property Type", active.property_type],
                      ["Email", active.email],
                      ["Phone", active.phone || "—"],
                    ].map(([k, v]) => (
                      <div key={k}>
                        <dt className="xw-label">{k}</dt>
                        <dd className="text-[15px] mt-1 break-words">{v}</dd>
                      </div>
                    ))}
                  </dl>

                  <div className="mt-8">
                    <div className="xw-label">Message</div>
                    <div className="mt-2 whitespace-pre-wrap text-[15px] leading-relaxed text-[var(--xw-ink-soft)] border border-[var(--xw-line-soft)] p-4 bg-[var(--xw-bg)] min-h-[120px]">
                      {active.message || "—"}
                    </div>
                  </div>

                  <div className="mt-8 flex items-center gap-3">
                    <a
                      href={`mailto:${active.email}`}
                      className="btn-solid"
                      data-testid="admin-reply-email"
                    >
                      Reply via Email
                    </a>
                    {active.phone && (
                      <a
                        href={`tel:${active.phone}`}
                        className="btn-outline"
                        data-testid="admin-call"
                      >
                        Call
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-[var(--xw-muted)]">Select a submission to view details.</div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
